/**
 * Web AccountLinking (settings → "Sign-in methods"; docs/auth/sign-in.md → "Account linking"):
 * - Linking a provider is Better Auth's redirect flow with the session cookie: POST /auth/link-social → the provider →
 *   /auth/callback/<id> → back to this page with `?linked=<provider>`, or `?linkError=<provider>&error=<code>`.
 * - Removing one: POST /auth/unlink-account. "Add email sign-in": POST /auth/email-otp/request-email-change, then
 *   /auth/email-otp/change-email.
 * - The list and "Use Google photo" are /v1 routes with the access token.
 * Desktop: everything but the list happens in the system browser (`openInBrowser`), like sign-in.
 */
import type { LinkedProvider, SignInMethodsResponse, UseProviderPhotoResponse } from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type AccountLinking, type ApiErrorKind, type LinkReturn, type SessionViewModel } from "@bundle/core";
import type { AccountLinkingContextValue } from "@bundle/ui";
import { authCall, dropParams, withMarker } from "./auth.ts";
import type { Container } from "./container.ts";
import { desktopInfo, isDesktop } from "./desktop.ts";
import { API_BASE } from "./env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

/** A /v1 call with the session's access token (refreshed first; once more after a 401). */
export async function v1<T>(session: SessionViewModel, name: RouteName, params: Record<string, string>, base: string, body?: unknown): Promise<T> {
  const route = routes[name];
  const send = async (token: string | undefined) => {
    const headers: Record<string, string> = { accept: "application/json" };
    if (body !== undefined) headers["content-type"] = "application/json";
    if (token !== undefined && token !== "") headers.authorization = `Bearer ${token}`;
    try {
      return await fetch(base + buildPath(route.path, params), { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    } catch (e) {
      throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
    }
  };
  await session.ensureFreshToken();
  let token = session.state.session?.token;
  let res = await send(token);
  if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
    token = session.state.session?.token;
    res = await send(token);
  }
  const text = await res.text();
  let json: unknown;
  try {
    json = text === "" ? undefined : JSON.parse(text);
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
    throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
  }
  return json as T;
}

/** What a linking redirect brought back to this page (read once; the markers leave the address bar). */
export function takeLinkReturn(): LinkReturn | undefined {
  const q = new URL(location.href).searchParams;
  const linked = q.get("linked");
  const failed = q.get("linkError");
  const provider = (failed ?? linked) as LinkedProvider | null;
  if (provider !== "google" && provider !== "apple") return undefined;
  const error = failed === null ? undefined : (q.get("error") ?? "failed");
  dropParams("linked", "linkError", "error", "error_description");
  return error === undefined ? { provider } : { provider, error };
}

export function createAccountLinking(session: SessionViewModel, base: string = API_BASE): AccountLinking {
  const linking: AccountLinking = {
    list: () => v1<SignInMethodsResponse>(session, "signInMethods", {}, base),
    async link(provider, returnTo) {
      const res = await authCall<{ url?: string }>("POST", "/link-social", {
        provider,
        callbackURL: withMarker(returnTo, "linked", provider),
        errorCallbackURL: withMarker(returnTo, "linkError", provider),
        disableRedirect: true,
      });
      if (typeof res.url !== "string") throw new ApiFailure("server", 500, "no_url", "Linking did not return a URL");
      window.location.assign(res.url);
      return "redirected";
    },
    async unlink(accountId) {
      await authCall("POST", "/unlink-account", { accountId });
    },
    async sendEmailCode(email) {
      await authCall("POST", "/email-otp/request-email-change", { newEmail: email });
    },
    async verifyEmailCode(email, code) {
      await authCall("POST", "/email-otp/change-email", { newEmail: email, otp: code });
    },
    async applyProviderPhoto(accountId) {
      return (await v1<UseProviderPhotoResponse>(session, "useProviderPhoto", { accountId }, base, {})).workspaces;
    },
  };
  if (isDesktop()) {
    // The desktop app has no browser session: linking happens on the same settings page in the system browser.
    linking.openInBrowser = async () => {
      const web = desktopInfo()?.webUrl ?? "https://app.bundle.direct";
      window.open(`${web}${location.pathname}`, "_blank", "noopener");
    };
  }
  return linking;
}

/** Account linking for the app shell (AccountLinkingContext): the real API, or the in-browser mock (code-split). */
export function linkingContext(container: Container): AccountLinkingContextValue {
  const session = container.session;
  let linking: AccountLinking;
  if (container.mock) {
    const ready = import("./mock/linking.ts").then(({ createMockLinking }) =>
      createMockLinking(() => ({ userId: session.state.session?.userId ?? "", email: session.state.session?.email ?? "" })),
    );
    linking = {
      list: () => ready.then((l) => l.list()),
      link: (provider, returnTo) => ready.then((l) => l.link(provider, returnTo)),
      unlink: (accountId) => ready.then((l) => l.unlink(accountId)),
      sendEmailCode: (email) => ready.then((l) => l.sendEmailCode(email)),
      verifyEmailCode: (email, code) => ready.then((l) => l.verifyEmailCode(email, code)),
      applyProviderPhoto: (accountId) => ready.then((l) => l.applyProviderPhoto(accountId)),
    };
  } else {
    linking = createAccountLinking(session);
  }
  return {
    linking,
    api: container.api,
    returnTo: () => location.pathname,
    takeReturn: takeLinkReturn,
    userId: () => session.state.session?.userId ?? "",
  };
}
